/**
 *
 * @module deviceGrantStorage
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';

const Util = Services.Util;
const Storage = Services.Storage;
const DeviceGrant = Services.Token.DeviceGrant;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class DeviceGrantStorage
 * @desc Provides a storage mechanism for storing DeviceGrant.
 *
 */
export default class DeviceGrantStorage {

    /**
     *
     * @param {String} clientId
     * @param {String} environment
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Services.Token.DeviceGrant} [deviceGrant=null]
     * @param {SDK.Services.Storage} [storage=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(clientId, environment, logger = Logging, deviceGrant = null, storage = null) {

        const errorMessage = {
            ctor: 'DeviceGrantStorage(clientId, environment, logger, deviceGrant, storage)',
            get clientId() {
                return `${this.ctor} clientId should be a String with length`;
            },
            get environment() {
                return `${this.ctor} environment should be a String with length`;
            },
            get deviceGrant() {
                return `${this.ctor} needs SDK.Services.Token.DeviceGrant instance`;
            },
            get storage() {
                return `${this.ctor} needs SDK.Services.Storage instance`;
            }
        };


        nonEmptyString(clientId, errorMessage.clientId, InvalidArgumentException);
        nonEmptyString(environment, errorMessage.environment, InvalidArgumentException);

        if (Check.assigned(deviceGrant)) {
            instanceStrict(deviceGrant, DeviceGrant, errorMessage.deviceGrant, InvalidArgumentException);
        }

        if (Check.assigned(storage)) {
            instanceStrict(storage, Storage, errorMessage.storage, InvalidArgumentException);
        }

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.clientId = clientId;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.environment = environment;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Services.Token.DeviceGrant|Null}
         *
         */
        this.deviceGrant = null;

        /**
         *
         * @access private
         * @type {SDK.Services.Storage}
         *
         */
        this.storage = storage || new Storage(this.logger);

        /**
         *
         * @access private
         * @type {String}
         * @desc cache key scoped under client ID and environment to prevent clashes,
         * maintains the same structure as all other cacheKey(s) in the SDK
         *
         */
        this.cacheKey = `
            __bam_sdk_device_grant--${this.clientId}_${this.environment}
        `.replace(/(?:\n\s+)/g, '');

        this.logger.log(DeviceGrantStorage.name, 'Created.');

        /**
         *
         * @note save DeviceGrant if passed in as an argument, otherwise
         * attempt to fetch from the {SDK.Services.Storage} implementation
         *
         */
        if (Check.assigned(deviceGrant)) {

            this.saveDeviceGrant(deviceGrant);

        } else {

            this.getDeviceGrantFromStorage();

        }

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.DeviceGrant} deviceGrant
     * @desc stores a device grant in Storage
     * @returns {Promise<Void>}
     *
     */
    saveDeviceGrant(deviceGrant) {

        const errorMessage = {
            method: 'DeviceGrantStorage.saveDeviceGrant(deviceGrant)',
            get deviceGrant() {
                return `${this.method} needs SDK.Services.Token.DeviceGrant instance`;
            }
        };

        instanceStrict(deviceGrant, DeviceGrant, errorMessage.deviceGrant, InvalidArgumentException);

        const { cacheKey, logger, storage } = this;

        logger.info(DeviceGrantStorage.name, `Save device grant to Storage using key: "${cacheKey}".`);

        this.deviceGrant = deviceGrant;

        return storage.set(cacheKey, this.deviceGrant);

    }

    /**
     *
     * @access private
     * @desc returns the current device grant from Storage
     * @returns {SDK.Services.Token.DeviceGrant}
     *
     */
    getDeviceGrant() {

        const { logger } = this;

        logger.log(DeviceGrantStorage.name, 'Get cached device grant.');

        return this.deviceGrant;

    }

    /**
     *
     * @access private
     * @desc deletes the current device grant from Storage
     * @returns {Promise<Void>}
     *
     */
    clear() {

        const { cacheKey, logger, storage } = this;

        logger.warn(DeviceGrantStorage.name, `Clearing cached device grant for key: "${cacheKey}".`);

        this.deviceGrant = null;

        return storage.remove(cacheKey);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc executed a single time on DeviceGrantStorage instantiation
     * checks Storage to determine if a device grant was previously stored,
     * should fail silently if no device grant found
     * @returns {Promise<SDK.Services.Token.DeviceGrant>}
     *
     */
    getDeviceGrantFromStorage() {

        const { cacheKey, logger, storage } = this;

        logger.info(DeviceGrantStorage.name, `Get device grant from Storage using cache key: "${cacheKey}".`);

        return storage.get(cacheKey).catch(() => {

            return Promise.resolve(this.deviceGrant);

        }).then((deviceGrant) => {

            const grantType = Util.getSafe(() => deviceGrant.grantType);
            const assertion = Util.getSafe(() => deviceGrant.assertion);

            if (Check.assigned(grantType)) {
                this.deviceGrant = new DeviceGrant(grantType, assertion);
            }

            return Promise.resolve(this.deviceGrant);

        });

    }

}



// WEBPACK FOOTER //
// ./src/token/deviceGrantStorage.js